// Copyright 2024 Sorama B.V.

import { EventAggregator } from "aurelia-event-aggregator";
import { autoinject, Disposable, transient } from "aurelia-framework";
import { WorkflowModalCatalog } from "catalogs/WorkflowModalCatalog";
import { Toast } from "controls/toast/Toast";
import { GenericMeasurement, Point3D } from "datatypes/SoramaWebApi";
import { MeasurementExtensions } from "extensions/MeasurementExtensions";
import { Point3DExtension } from "extensions/Point3DExtension";
import {
    IWorkflowModuleActivate,
    IWorkflowModuleDeactivate,
    IWorkflowModuleInstance,
} from "libraries/WorkflowFramework/Interfaces/IWorkflowModule";
import { WorkflowPropertyBindingMode } from "libraries/WorkflowFramework/Interfaces/IWorkflowPropertyMetadata";
import { workflowProperty } from "libraries/WorkflowFramework/WorkflowProperties/WorkflowPropertyAttribute";
import {
    PsychoAnalysisModalReturnState,
    PsychoAnalysisModalState,
} from "modules/modals/PsychoAnalysis/PsychoAnalysisModal";
import { MeasurementRepository } from "repositories/MeasurementRepository";
import { IMeasurementProcessorResolver } from "services/Measurement/IMeasurementProcessorResolver";
import { v4 as uuidv4 } from "uuid";
import {
    AllPsychoAcousticMetricTypes,
    PsychoAcousticAnalysisResultPerMeasurement,
    PsychoAcousticMetricType,
} from "./SpiderChart/SpiderChart";

@transient()
@autoinject
export class PsychoAcousticsModule implements IWorkflowModuleActivate, IWorkflowModuleDeactivate {
    // Collection of the analysis results, containing microphone properties, and psychoacoustic values.
    public psychoAcousticAnalysisResults: Partial<PsychoAcousticAnalysisResultPerMeasurement> = {};
    public psychoAcousticMetrics: PsychoAcousticMetricType[] = AllPsychoAcousticMetricTypes;

    //#region Workflow Properties

    @workflowProperty({ bindingMode: WorkflowPropertyBindingMode.twoWay, objectType: "string" })
    public selectedMeasurementIds: string[] = [];

    @workflowProperty({ bindingMode: WorkflowPropertyBindingMode.twoWay, objectType: "string" })
    public selectedPsychoAcousticMeasurementIds: string[] = [];

    @workflowProperty({ bindingMode: WorkflowPropertyBindingMode.twoWay, objectType: "Point3D" })
    public selectedMic: Point3D;

    //#endregion
    public measurements: GenericMeasurement[];

    // Generate a unique id for the toast
    public toastId: string = uuidv4();
    public spiderChartId: string = uuidv4();

    protected isSelectingMics = true;
    protected subscriptions: Disposable[];
    private moduleInstance: IWorkflowModuleInstance;
    private measurementUpdateDisposables: Disposable[] = [];

    //#region Constructor and Initialization
    constructor(
        private readonly measurementRepository: MeasurementRepository,
        private readonly eventAggregator: EventAggregator,
        private readonly processorResolver: IMeasurementProcessorResolver
    ) {
        this.subscriptions = [
            this.eventAggregator.subscribe("spider-chart:psycho-acoustic-results-ready", (computedResults) => {
                this.psychoAcousticAnalysisResults = computedResults;
            }),
        ];
    }

    public activateModule(moduleInstance: IWorkflowModuleInstance): void {
        this.moduleInstance = moduleInstance;

        this.selectedMeasurementIdsChanged();
    }

    public deactivateModule(): void {
        // Clear disposables
        while (this.measurementUpdateDisposables.length > 0) {
            this.measurementUpdateDisposables.pop().dispose();
        }

        // Unsubscribe from the existing subscriptions
        if (this.subscriptions) {
            this.subscriptions.map((x) => x.dispose());
        }
    }
    //#endregion

    // Close MicSelection and open SpiderChart
    public onContinue() {
        this.isSelectingMics = false;
    }

    public onBack() {
        this.eventAggregator.publish("clearSVG-" + this.spiderChartId);
        this.isSelectingMics = true;
    }

    public selectMetric(metric: PsychoAcousticMetricType): void {
        let config = WorkflowModalCatalog.PsychoAnalysisModal;

        // Set title of the psycho analysis
        config.title = metric;

        this.moduleInstance
            .createModal<PsychoAnalysisModalState, PsychoAnalysisModalReturnState>(config)
            .withState({
                measurements: this.measurements,
                psychoAcousticAnalysisResults: this.psychoAcousticAnalysisResults,
                selectedMeasurementIds: this.selectedMeasurementIds,
                metric: metric,
            })
            .show();
    }

    /**
     * Handles changes to selected measurement IDs by fetching and setting microphone positions and updating observers.
     */
    private async selectedMeasurementIdsChanged(): Promise<void> {
        // Make sure all modules following this psychoacoustics module (psychoacoustic further analysis modules) are also updated
        this.selectedPsychoAcousticMeasurementIds = this.selectedMeasurementIds;

        if (this.moduleInstance) {
            this.moduleInstance.cancelModal();
        }

        let measurements = await Promise.all(
            this.selectedMeasurementIds.map((id) => this.measurementRepository.getMeasurement(id))
        );

        // Check if all measurements are of the same device type
        if (measurements.length > 0) {
            const groundTruth = measurements[0];
            const matchingMeasurements = MeasurementExtensions.getMeasurementsWithSameMicPositions(
                groundTruth,
                measurements
            );
            if (matchingMeasurements.length !== measurements.length) {
                // Show a message that the measurements are not on the same device type
                // TODO-SUS-514: Use workflowmoduleinstance to automatically show thrown errors as toast
                Toast.error(
                    "Cannot compare measurements of different types of acoustic cameras.",
                    "Unable to add measurement",
                    this.toastId
                );
                // Unselect the missing measurements
                this.selectedMeasurementIds = matchingMeasurements.map((m) => m.id);
            }

            // Check if the measurement has the selected microphone position
            // If not, we changed device type --> Reprompt mic selection
            if (this.selectedMic) {
                const micPositions = MeasurementExtensions.getMicrophonePositionsForMeasurement(groundTruth);
                if (!micPositions.some((micPos) => Point3DExtension.Equals(micPos, this.selectedMic))) {
                    this.selectedMic = undefined;
                    // TODO-SUS-514: Use workflowmoduleinstance to call toast message
                    Toast.info(
                        "Microphone configuration has changed. Please reselect the microphone.",
                        "Select microphone",
                        this.toastId
                    );
                    this.onBack();
                }
            }

            measurements = matchingMeasurements;
        }
        // Clear measurement update observers
        while (this.measurementUpdateDisposables.length > 0) {
            this.measurementUpdateDisposables.pop().dispose();
        }
        // Trigger update when measurements are updated
        this.measurements = measurements;
    }

    protected getButtonState(
        metric: PsychoAcousticMetricType,
        measurements: GenericMeasurement[]
    ): { isDisabled: boolean; tooltip: string } {
        if (metric === "Fluctuation Strength" && measurements.every((m) => m.duration < 5)) {
            return {
                isDisabled: true,
                tooltip: "This measurement is too short. Please use a measurement of at least 5 seconds.",
            };
        }

        return {
            isDisabled: false,
            tooltip: `Analyze the ${metric} of the selected measurements.`,
        };
    }
}
